// src/components/ActiveBookingsPanel.jsx
import React, { useState, useEffect, useCallback } from "react";
import carImg from "../assets/car.png";
import cabImg from "../assets/cab.png";
import busImg from "../assets/bus.png";
import vanImg from "../assets/van.png";
import lorryImg from "../assets/lorry.png";
import suvImg from "../assets/suv.png";
import cherryImg from "../assets/cherry.png";
import dockyardImg from "../assets/dockyard.png";
import forkliftImg from "../assets/fork lift.png";
import craneImg from "../assets/crane.jpg";
// Helper to get vehicle icon by type (supports both codes and full names)
const getVehicleIcon = (type = "") => {
  const t = type.toUpperCase().trim();
  // Match by code first, then by name
  if (t === "B" || t.includes("BUS")) return busImg;
  if (t === "CA" || t.includes("CAB") || t.includes("TAXI")) return cabImg;
  if (t === "V" || t.includes("VAN")) return vanImg;
  if (t === "L" || t.includes("LORRY") || t.includes("TRUCK")) return lorryImg;
  if (t === "S" || t.includes("SUV")) return suvImg;
  if (t === "CH" || t.includes("CHERRY")) return cherryImg;
  if (t.includes("DOCKYARD")) return dockyardImg;
  if (t === "F" || t.includes("FORK") || t.includes("FORKLIFT")) return forkliftImg;
  if (t === "C" || t.includes("CAR")) return carImg;
  if (t === "CR" || t.includes("CRANE")) return craneImg;
  return carImg;
};
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchBookings } from "../actions/BookingActions";
import { fetchVehicles } from "../actions/VehicleActions";

import { calculateDistance } from "../utils/calculateDistance";

const ActiveBookingsPanel = ({ driverSno, isOnline }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeBookings, setActiveBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedBookingId, setExpandedBookingId] = useState(null);

  const normalizeStatus = (status) => String(status || "").trim().toUpperCase();

  const isActiveStatus = (status) => {
    const s = normalizeStatus(status);
    return (
      s === "A" ||
      s === "ACTIVE" ||
      s === "EN" ||
      s === "ENROUTE" ||
      s === "E" ||
      s === "IP" ||
      s === "INPROGRESS" ||
      s === "IN PROGRESS" ||
      s === "P" ||
      s === "PENDING" ||
      s === "R"
    );
  };

  const convertResourceCodeToResourceType = (resourceCode) => {
    switch (resourceCode) {
      case "T":
        return "Trailer"
        break;
      case "C":
        return "Crane";
        break;
      case "TC":
        return "Trailer and Crane";
        break;
      default:
        return null  
    }
  }

  // Fetch active bookings for the current driver, then fetch vehicle details and attach vehicleType
  const fetchActiveBookings = useCallback(async () => {
    if (!driverSno) return;

    setIsLoading(true);
    try {
      const allBookings = await dispatch(fetchBookings());

      // Filter for active bookings (includes Pending + R) belonging to this driver
      const driverActiveBookings = allBookings.filter(
        (booking) => {
          return booking.dSNo === driverSno && isActiveStatus(booking.status);
        }
      );

      const vehicles = await dispatch(fetchVehicles());

      // Attach correct vehicleType to each booking
      const bookingsWithType = driverActiveBookings.map((booking) => {
        const vId = booking.vId || booking.vehicleId;
        const vehicle = vehicles.find(v => v.vehicleId?.toString() === vId?.toString());
        return {
          ...booking,
          vehicleType: vehicle?.vehicleType || booking.vehicleType || booking.vType || booking.type || "",
        };
      });

      setActiveBookings(bookingsWithType);
    } catch (err) {
      console.error("Failed to fetch active bookings:", err);
      toast.error("Failed to load active bookings");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, driverSno]);

  // Fetch bookings on component mount and when driverSno changes
  useEffect(() => {
    fetchActiveBookings();
    
    // Refresh every 30 seconds to check for completed bookings
    const interval = setInterval(fetchActiveBookings, 30000);
    return () => clearInterval(interval);
  }, [fetchActiveBookings]);

  // const calculateDistance = (lat1, lon1, lat2, lon2) => {
  //   // Convert to numbers in case they come as strings from API
  //   const lat1Num = parseFloat(lat1);
  //   const lon1Num = parseFloat(lon1);
  //   const lat2Num = parseFloat(lat2);
  //   const lon2Num = parseFloat(lon2);
  //   if (
  //     Number.isNaN(lat1Num) ||
  //     Number.isNaN(lon1Num) ||
  //     Number.isNaN(lat2Num) ||
  //     Number.isNaN(lon2Num)
  //   ) {
  //     return null;
  //   }

  //   const R = 6371; // Earth's radius in km
  //   const dLat = ((lat2Num - lat1Num) * Math.PI) / 180;
  //   const dLon = ((lon2Num - lon1Num) * Math.PI) / 180;
  //   const a =
  //     Math.sin(dLat / 2) * Math.sin(dLat / 2) +
  //     Math.cos((lat1Num * Math.PI) / 180) *
  //       Math.cos((lat2Num * Math.PI) / 180) *
  //       Math.sin(dLon / 2) *
  //       Math.sin(dLon / 2);
  //   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  //   return (R * c).toFixed(2);
  // };

  const formatBookingTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  const formatBookingDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Handle tracking/resuming trip
  const handleTrackTrip = (booking) => {
    // Navigate to DriverTrip page - only pass bId, page will fetch fresh data from API
    console.log("[ActiveBookingsPanel] Tracking trip for booking:", booking.bId);
    navigate(`/driver-trip/${booking.bId}`);
  };

  const getStatusLabel = (booking) => {
    const s = normalizeStatus(booking.status);
    if (s === "P" || s === "PENDING") return "Pending";
    if (s === "R") return "R";
    if (s === "A" || s === "ACTIVE") return "Active";
    if (s === "EN" || s === "ENROUTE" || s === "E") return "Enroute";
    if (s === "IP" || s === "INPROGRESS" || s === "IN PROGRESS") return "In Progress";
    return booking.status || "Unknown";
  };

  const getStatusBadgeColor = (booking) => {
    const s = normalizeStatus(booking.status);
    if (s === "P" || s === "PENDING") return "bg-yellow-100 text-yellow-800";
    if (s === "R") return "bg-blue-100 text-blue-800";
    if (s === "A" || s === "ACTIVE") return "bg-green-100 text-green-700";
    if (s === "EN" || s === "ENROUTE" || s === "E") return "bg-purple-100 text-purple-700";
    if (s === "IP" || s === "INPROGRESS" || s === "IN PROGRESS") return "bg-indigo-100 text-indigo-700";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-3 sm:p-4 md:p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
            </svg>
            <h2 className="text-base sm:text-lg md:text-xl font-bold">
              Active Bookings
            </h2>
          </div>
          <div className="bg-white bg-opacity-30 px-3 py-1 rounded-full">
            <span className="text-xs sm:text-sm font-semibold">
              {activeBookings.length} {activeBookings.length === 1 ? "Trip" : "Trips"}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5 md:p-6">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : activeBookings.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-3 sm:mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-gray-500 text-sm sm:text-base">
              No active bookings at the moment
            </p>
            <p className="text-gray-400 text-xs sm:text-sm mt-2">
              {isOnline
                ? "Go online to receive new trips"
                : "Come online to accept new bookings"}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {activeBookings.map((booking) => {
              const distance = calculateDistance(
                booking.slatitude,
                booking.slongitude,
                booking.elatitude,
                booking.elongitude
              );
              const isExpanded = expandedBookingId === booking.bId;
              const vehicleType = booking.vehicleType || booking.vType || booking.type || "";
              const vehicleIcon = getVehicleIcon(vehicleType);
              const fromValue = booking.From || booking.fromLocation || booking.WorkArea || "";
              const toValue = booking.TO || booking.toLocation || "";
              const workScopeValue = booking.WorkScope || booking.workScope || "";
              const priorityValue = booking.Priority || booking.priority || "";
              const remarkValue = booking.Remark || booking.remark || "";
              const resourceCode = booking.SubBook;

              const resourceType = convertResourceCodeToResourceType(resourceCode);

              return (
                <div
                  key={booking.bId}
                  className="border border-green-200 rounded-lg overflow-hidden transition-all hover:shadow-md"
                >
                  {/* Booking Card Header */}
                  <button
                    onClick={() =>
                      setExpandedBookingId(isExpanded ? null : booking.bId)
                    }
                    className="w-full p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <img src={vehicleIcon} alt={vehicleType} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-gray-200 bg-white object-contain" style={{background:'#fff'}} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="bg-green-600 text-white text-xs sm:text-sm font-bold px-2 sm:px-3 py-1 rounded-full flex-shrink-0">
                              Trip #{booking.bId}
                            </span>
                            <span className={`text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full flex-shrink-0 ${getStatusBadgeColor(booking)}`}>
                              {getStatusLabel(booking)}
                            </span>
                          </div>
                          {distance ? (
                            <p className="text-xs sm:text-sm text-gray-700 truncate">
                              <strong>Distance:</strong> {distance} km
                            </p>
                          ) : (
                            <p className="text-xs sm:text-sm text-gray-700 truncate">
                              <strong>Route:</strong> {fromValue || "N/A"} {toValue ? "-> " + toValue : ""}
                            </p>
                          )}

                          {resourceType && (
                            <p className="text-xs sm:text-sm text-gray-700 truncate">
                              <strong>Resource Type:</strong> {resourceType}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1">
                          {formatBookingTime(booking.bsTime || booking.bookingDT)}
                        </p>
                        <svg
                          className={`w-5 h-5 sm:w-6 sm:h-6 text-gray-600 transition-transform ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 14l-7 7m0 0l-7-7m7 7V3"
                          />
                        </svg>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-green-200 p-3 sm:p-4 bg-white space-y-3 sm:space-y-4">
                      {/* Pickup Location */}
                      <div className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <svg
                              className="w-4 h-4 sm:w-5 sm:h-5 text-green-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-semibold text-gray-700">
                            Pickup Location
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            {booking.slatitude && booking.slongitude
                              ? `Lat: ${parseFloat(booking.slatitude)?.toFixed(4)}°`
                              : `From: ${fromValue || "N/A"}`}
                            {booking.slatitude && booking.slongitude && (
                              <>
                                <br />
                                {`Lon: ${parseFloat(booking.slongitude)?.toFixed(4)}°`}
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Distance Indicator */}
                      {distance && (
                        <div className="flex items-center justify-center py-2">
                          <div className="flex-1 h-1 bg-green-200"></div>
                          <div className="mx-3 px-3 py-1 bg-green-100 rounded-full">
                            <p className="text-xs sm:text-sm font-semibold text-green-700">
                              {distance} km
                            </p>
                          </div>
                          <div className="flex-1 h-1 bg-green-200"></div>
                        </div>
                      )}

                      {/* Dropoff Location */}
                      <div className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg
                              className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-semibold text-gray-700">
                            Dropoff Location
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            {booking.elatitude && booking.elongitude
                              ? `Lat: ${parseFloat(booking.elatitude)?.toFixed(4)}°`
                              : `To: ${toValue || "N/A"}`}
                            {booking.elatitude && booking.elongitude && (
                              <>
                                <br />
                                {`Lon: ${parseFloat(booking.elongitude)?.toFixed(4)}°`}
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Booking Details Grid */}
                      <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-2 border-t border-gray-100">
                        <div className="bg-gray-50 p-2 sm:p-3 rounded">
                          <p className="text-xs text-gray-600">Vehicle</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 mt-1">
                            {booking.vId || booking.vehicleId || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-2 sm:p-3 rounded">
                          <p className="text-xs text-gray-600">Booking Time</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 mt-1">
                            {formatBookingTime(booking.bookingDT)}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-2 sm:p-3 rounded">
                          <p className="text-xs text-gray-600">Start Time</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 mt-1">
                            {formatBookingTime(booking.bsTime)}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-2 sm:p-3 rounded">
                          <p className="text-xs text-gray-600">Booking ID</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 mt-1">
                            {booking.bookingSno || booking.bId}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-2 sm:p-3 rounded">
                          <p className="text-xs text-gray-600">Status</p>
                          <p className="text-xs sm:text-sm font-semibold text-green-700 mt-1">
                            {getStatusLabel(booking)}
                          </p>
                        </div>
                        {workScopeValue && (
                          <div className="bg-gray-50 p-2 sm:p-3 rounded col-span-2">
                            <p className="text-xs text-gray-600">Work Scope</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 mt-1">
                              {workScopeValue}
                            </p>
                          </div>
                        )}
                        {priorityValue && (
                          <div className="bg-gray-50 p-2 sm:p-3 rounded">
                            <p className="text-xs text-gray-600">Priority</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 mt-1">
                              {priorityValue}
                            </p>
                          </div>
                        )}
                        {remarkValue && (
                          <div className="bg-gray-50 p-2 sm:p-3 rounded">
                            <p className="text-xs text-gray-600">Remark</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 mt-1">
                              {remarkValue}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 sm:gap-3 mt-2 sm:mt-3">
                        <button
                          onClick={() => handleTrackTrip(booking)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg transition-colors text-xs sm:text-sm flex items-center justify-center gap-2"
                        >
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          <span>Track Trip</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="border-t border-gray-100 px-4 sm:px-5 md:px-6 py-3 sm:py-4 bg-gray-50 flex justify-end">
        <button
          onClick={fetchActiveBookings}
          disabled={isLoading}
          className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50"
        >
          <svg
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>
    </div>
  );
};

export default ActiveBookingsPanel;
